import React, { useEffect, useState } from "react";
import EditStaffForm from "./EditStaffForm";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import { useNavigate, useParams } from "react-router-dom";
import images from "../../../assets/images/constantImages";
import AttendanceHistory from "./AttendanceHistory";
import icons from "../../../assets/images/constantIcons";
import ApplyLeaveForm from "./ApplyLeaveForm";
import Calendar from "./Calender";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import LeaveStatus from "./LeaveStatus";

const ViewStaff = () => {
  const [staffData, setStaffData] = useState(null); // initial state is null
  const [editPopup, setEditPopup] = useState(false);
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("AttendanceCalendar");
  const [profileUpdate, setProfileUpdate] = useState(false);
  const [image, setImage] = useState(null);
  const [showGenerateSlipForm, setShowGenerateSlipForm] = useState(false);
  const currentMonth = dayjs().month() + 1; // 1-12
  const currentYear = dayjs().year();
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getInitials = (fullName) => {
    if (!fullName) return "";
    const nameParts = fullName.trim().split(" ");
    if (nameParts.length === 1) return nameParts[0][0].toUpperCase();
    return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
  };

  // Render content based on activeTab
  const renderContent = () => {
    switch (activeTab) {
      case "AttendanceCalendar":
        return <Calendar staffId={id} />;
      case "AttendanceHistory":
        return <AttendanceHistory staffId={id} />;
      case "applyLeave":
        return <ApplyLeaveForm staffId={id} />;
      case "leaveStatus":
        return <LeaveStatus staffId={id} statusFilter="pending" />;
      case "leaveHistory":
        return <LeaveStatus staffId={id} statusFilter="history" />;
      default:
        return null;
    }
  };

  const handleEditPopup = () => {
    setEditPopup(!editPopup);
  };

  const fetchStaffDetails = async () => {
    try {
      const response = await apiService.get(`/staff_users/${id}`);
      setStaffData(response.data);
    } catch (errors) {
      handleError(errors);
    }
  };

  // update profile image
  const handleImageChange = (e) => {
    setImage(e.target.files[0]);
  };

  const handleUpdateProfileImage = async () => {
    try {
      if (!image) return;
      const formData = new FormData();
      formData.append("staff_user[image]", image);
      const response = await apiService.put(`/staff_users/${id}`, formData);
      setStaffData((prev) => ({ ...prev, image: response.data.image }));
      toast.success("Profile image updated successfully!");
      setProfileUpdate(false);
    } catch (errors) {
      handleError(errors);
    }
  };

  const fullImageUrl = staffData?.image
    ? `https://headquater.pixoatic.com${staffData.image}`
    : images.defaultprofilepicture;

  // Fetch salary slip based on month/year

  const fetchSalarySlipByMonth = async () => {
    setLoading(true);
    try {
      const payload = {
        salary: {
          month: selectedMonth,
          year: selectedYear,
          staff_user_id: Number(id),
        },
      };

      const response = await apiService.post("/salary_slips", payload, {
        responseType: "blob",
        validateStatus: () => true, // handle all responses
      });

      if (response.status !== 200) {
        const errorText = await response.data.text();
        try {
          const { errors } = JSON.parse(errorText);
          toast.error(errors, { duration: 2000 });
        } catch {
          toast.error(errorText, { duration: 2000 });
        }
        return;
      }

      //  download the file
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${staffData?.full_name}_SalarySlip_${selectedMonth}_${selectedYear}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Salary slip downloaded successfully!", { duration: 2000 });
      setShowGenerateSlipForm(false);
    } catch (errors) {
      handleError(errors);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchStaffDetails();
    }
  }, [id]);

  const handleGenerateSalarySlip = () => {
    fetchSalarySlipByMonth();
    setShowGenerateSlipForm(false);
  };

  // staff details
  const staffDetails = [
    { label: "Contact", value: staffData?.mobile_no },
    { label: "Email", value: staffData?.email },
    { label: "Location", value: staffData?.location },
    { label: "Salary", value: staffData?.base_salary },
    { label: "Role", value: staffData?.role ? staffData.role.name : "" },
    { label: "Joining Date", value: staffData?.joining_date },
  ];
  const joiningYear = new Date(staffData?.joining_date).getFullYear();
  const yearsToShow =
    currentYear === joiningYear
      ? [currentYear]
      : Array.from(
          { length: currentYear - joiningYear + 1 },
          (_, i) => joiningYear + i
        );

  if (staffData === null) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 py-4 w-full">
      <div className="flex flex-col items-center gap-8">
        {/* Staff Details Section */}
        <div className="flex flex-col w-full bg-white rounded-xl">
          <icons.IoMdArrowRoundBack
            onClick={() => navigate(-1)}
            className="cursor-pointer text-green text-2xl font-extrabold ml-6 my-2"
          />

          <div className="w-full flex flex-col items-start xl:flex-row justify-between pt-0 p-6">
            <div className="flex flex-col md:flex-row md:gap-7 gap-3">
              <div className="relative w-[100px] md:w-auto">
                {staffData?.image ? (
                  <img
                    src={fullImageUrl}
                    alt="Profile"
                    onError={() =>
                      setStaffData((prev) => ({ ...prev, image: null }))
                    }
                    className="w-[100px] h-[100px] object-cover rounded-full border-2 border-white shadow-xl mb-4"
                  />
                ) : (
                  <div className="w-[100px] h-[100px] flex items-center justify-center bg-blueButton text-white text-2xl font-semibold rounded-full border-2 border-white shadow-xl mb-4">
                    {getInitials(staffData?.full_name)}
                  </div>
                )}
                <div
                  onClick={() => setProfileUpdate(true)}
                  className="flex justify-center items-center w-9 h-9 p-1 bg-green50 rounded-full absolute bottom-5 right-0 border border-white cursor-pointer"
                >
                  <icons.TbEdit className="text-green text-2xl" />
                </div>
              </div>
              <div className="flex flex-col items-start gap-2">
                <div className="flex justify-between">
                  <div className="flex gap-3 text-2xl font-semibold text-G500 items-center">
                    <span className="text-xl">{staffData?.full_name}</span>
                    <div className="bg-purple bg-opacity-10 px-1 h-5 rounded-xl">
                      <p className="text-sm text-purple">
                        {staffData?.status === "active" ? "Active" : "Deactive"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-1 md:gap-2 w-full mt-4">
                  {staffDetails?.map((detail, index) => (
                    <div key={index} className="flex gap-1 items-center">
                      <p className="text-xs sm:text-sm font-medium text-G500">
                        {detail.label}:
                      </p>
                      <p className="text-xs sm:text-sm font-normal text-G500">
                        {detail.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 group mt-3 lg:mt-4">
              <div className="flex gap-4 justify-start items-start">
                <button
                  className="md:ml-[120px] bg-indigo bg-opacity-10 text-indigo font-medium md:px-6 py-[6px] px-2 text-sm xl:text-base rounded-lg"
                  onClick={() => handleEditPopup()}
                >
                  Edit
                </button>
                <div
                  onClick={() => setShowGenerateSlipForm(true)}
                  className="bg-blue bg-opacity-10 text-blue font-medium py-[6px] px-3 text-sm xl:text-base rounded-lg w-auto flex justify-between gap-5 items-center cursor-pointer"
                >
                  <div className="flex gap-2 truncate">
                    Download Salary Slip
                  </div>
                  <icons.FiDownload className="cursor-pointer hover:text-blue" />
                </div>
              </div>
              {showGenerateSlipForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white p-6 rounded-lg w-[310px] s:w-[350px] md:w-[460px]">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-G500">
                        Select Month and year
                      </h3>
                      <icons.IoClose
                        onClick={() => setShowGenerateSlipForm(false)}
                        className="cursor-pointer text-green700 text-xl"
                      />
                    </div>
                    <div className="flex gap-4 mb-4">
                      <select
                        value={selectedMonth}
                        onChange={(e) =>
                          setSelectedMonth(Number(e.target.value))
                        }
                        className="inputtext"
                      >
                        {[...Array(12)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {dayjs().month(i).format("MMMM")}
                          </option>
                        ))}
                      </select>

                      <select
                        value={selectedYear}
                        onChange={(e) =>
                          setSelectedYear(Number(e.target.value))
                        }
                        className="inputtext"
                      >
                        {yearsToShow.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex space-x-4 justify-end pt-6">
                      <button
                        onClick={() => setShowGenerateSlipForm(false)}
                        className=" border border-green700  text-green w-[100px] text-sm text-center py-2 rounded-lg "
                      >
                        Cancel
                      </button>

                      <button
                        onClick={handleGenerateSalarySlip}
                        className="bg-green700 text-white text-sm w-[100px] text-center py-2 rounded-lg "
                        disabled={loading}
                      >
                        {loading ? "Downloading..." : "Download"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* Edit Form Popup */}
              <EditStaffForm
                editPopup={editPopup}
                onClose={handleEditPopup}
                staffId={id}
                fetchStaffDetails={fetchStaffDetails}
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex justify-start items-start gap-3 md:gap-14  text-green text-xs sm:text-sm font-medium md:px-6">
            <button
              onClick={() => setActiveTab("AttendanceCalendar")}
              className={` ${
                activeTab === "AttendanceCalendar"
                  ? "border-b-2 border-green pb-4 inline-block "
                  : ""
              }`}
            >
              Attendance Calendar
            </button>
            <button
              onClick={() => setActiveTab("AttendanceHistory")}
              className={` ${
                activeTab === "AttendanceHistory"
                  ? "border-b-2 border-green pb-4 inline-block "
                  : ""
              }`}
            >
              Attendance History
            </button>
            <button
              onClick={() => setActiveTab("applyLeave")}
              className={` ${
                activeTab === "applyLeave"
                  ? "border-b-2 border-green pb-4 inline-block "
                  : ""
              }`}
            >
              Apply Leave
            </button>
            <button
              onClick={() => setActiveTab("leaveStatus")}
              className={` ${
                activeTab === "leaveStatus"
                  ? "border-b-2 border-green pb-4 inline-block "
                  : ""
              }`}
            >
              Leave Status
            </button>
            <button
              onClick={() => setActiveTab("leaveHistory")}
              className={` ${
                activeTab === "leaveHistory"
                  ? "border-b-2 border-green pb-4 inline-block "
                  : ""
              }`}
            >
              Leave History
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="w-full  flex flex-col">{renderContent()}</div>

        {/* Image Upload Modal */}
        {profileUpdate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-[350px] md:w-[460px] relative">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-G500">
                  Update Profile Image
                </h2>
                <div
                  className="absolute top-7 right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px]"
                  onClick={() => setProfileUpdate(false)}
                >
                  <icons.IoClose className="text-green700" />
                </div>
              </div>
              <Formik
                initialValues={{ image: null }}
                onSubmit={handleUpdateProfileImage}
              >
                {({ setFieldValue, isSubmitting }) => (
                  <Form className="flex flex-col">
                    <div className="flex flex-col items-center">
                      <img
                        src={image ? URL.createObjectURL(image) : fullImageUrl}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-full mb-2 border"
                      />
                    </div>
                    <label className="cursor-pointer text-G300 text-sm mb-2">
                      Upload Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      className="text-sm inputtext"
                      onChange={handleImageChange}
                    />
                    <div className="flex justify-end space-x-4 pt-4">
                      <button
                        type="button"
                        onClick={() => setProfileUpdate(false)}
                        className="w-[93px] text-sm py-2 border border-green text-green rounded-lg"
                      >
                        Close
                      </button>
                      <button
                        type="submit"
                        className={`w-[93px] text-sm py-2 rounded-lg ${
                          isSubmitting
                            ? "bg-green50 cursor-not-allowed"
                            : "bg-green text-white"
                        }`}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Updating..." : "Update"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewStaff;
